// src/components/AdminManagement.js
import React, { useState, useEffect } from 'react';
import {
  getFirestore,
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  getDoc,
  setDoc,
} from 'firebase/firestore';

import { getAuth, onAuthStateChanged } from 'firebase/auth';
import Swal from 'sweetalert2'; // Import SweetAlert2
import withReactContent from 'sweetalert2-react-content'; // For SweetAlert customization
import styles from './AdminManagement.module.css'; // Import CSS module

const MySwal = withReactContent(Swal); // Customize SweetAlert

const AdminManagement = () => {
  const [adminEmail, setAdminEmail] = useState('');
  const [admins, setAdmins] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(false); // Loading state
  const [businessSettings, setBusinessSettings] = useState({
    isManual: false,
    isOpen: false,
    open: '09:00',
    close: '19:00',
  });
  const [openTime, setOpenTime] = useState('09:00');
  const [closeTime, setCloseTime] = useState('19:00');

  // --- Discount codes state ---
  const [discounts, setDiscounts] = useState([]);
  const [newDiscount, setNewDiscount] = useState({
    code: '',
    type: 'percent', // 'percent' o 'fixed'
    value: 0,
    isTemporal: false,
    startDate: '',
    endDate: '',
    isEnabled: true,
  });


  const firestore = getFirestore();
  const auth = getAuth();

  useEffect(() => {
    const fetchAdmins = async () => {
      setLoading(true); // Start loading
      const q = collection(firestore, 'admins');
      const querySnapshot = await getDocs(q);
      const adminsList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAdmins(adminsList);
      setLoading(false); // End loading
    };

    const fetchBusinessSettings = async () => {
      const docRef = doc(firestore, 'businessSettings', 'main');
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const settings = docSnap.data();
        setBusinessSettings(settings);
        setOpenTime(settings.open || '09:00');
        setCloseTime(settings.close || '19:00');
        autoUpdateStoreStatus(settings); // Automatically update store status if needed
      } else {
        console.error('Business settings not found.');
      }
    };


        const fetchDiscounts = async () => {
      try {
        const q = collection(firestore, 'discountCodes');
        const querySnapshot = await getDocs(q);
        const discountsList = querySnapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setDiscounts(discountsList);
      } catch (error) {
        console.error('Error fetching discounts:', error);
      }
    };


    const checkAdmin = async (user) => {
      if (user) {
        const q = collection(firestore, 'admins');
        const querySnapshot = await getDocs(q);
        const adminsList = querySnapshot.docs.map((doc) => doc.data().email);
        setIsAdmin(adminsList.includes(user.email));
      }
    };

    fetchAdmins();
    fetchBusinessSettings();
    fetchDiscounts();

    onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      checkAdmin(user);
    });
  }, [firestore, auth]);

  const autoUpdateStoreStatus = (settings) => {
    if (!settings.isManual) {
      const currentTime = new Date();
      const currentHours = currentTime.getHours();
      const currentMinutes = currentTime.getMinutes();
  
      const [openHours, openMinutes] = settings.open.split(':').map(Number);
      const [closeHours, closeMinutes] = settings.close.split(':').map(Number);
  
      const currentTotalMinutes = currentHours * 60 + currentMinutes;
      const openTotalMinutes = openHours * 60 + openMinutes;
      const closeTotalMinutes = closeHours * 60 + closeMinutes;
  
      const isOpen =
        currentTotalMinutes >= openTotalMinutes &&
        currentTotalMinutes <= closeTotalMinutes;
  
      // Only update if the status has changed
      if (settings.isOpen !== isOpen) {
        handleUpdateBusinessSettings({ isOpen });
      }
    }
  };
  
    const normalizeDiscountCode = (code) =>
    code ? code.trim().toUpperCase() : '';

  const handleNewDiscountChange = (field, value) => {
    setNewDiscount((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDiscountFieldChange = (id, field, value) => {
    setDiscounts((prev) =>
      prev.map((d) =>
        d.id === id ? { ...d, [field]: value } : d
      )
    );
  };


  const handleAddAdmin = async () => {
    // Email validation regex
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!adminEmail || !emailPattern.test(adminEmail)) {
      MySwal.fire('Error', 'Por favor, ingresa una dirección de correo válida.', 'warning');
      return;
    }

    setLoading(true); // Start loading spinner
    try {
      await addDoc(collection(firestore, 'admins'), { email: adminEmail });
      setAdminEmail('');
      MySwal.fire('Éxito', '¡Admin agregado exitosamente!', 'success');

      // Refresh the admin list
      const q = collection(firestore, 'admins');
      const querySnapshot = await getDocs(q);
      const adminsList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAdmins(adminsList);
    } catch (error) {
      MySwal.fire('Error', 'Hubo un problema al agregar el admin.', 'error');
    } finally {
      setLoading(false); // End loading spinner
    }
  };

    const handleAddDiscount = async () => {
    const codeNormalized = normalizeDiscountCode(newDiscount.code);

    if (!codeNormalized) {
      MySwal.fire('Error', 'Ingresa un código de descuento.', 'warning');
      return;
    }

    const valueNumber = Number(newDiscount.value);
    if (!valueNumber || valueNumber <= 0) {
      MySwal.fire('Error', 'Ingresa un valor de descuento válido.', 'warning');
      return;
    }

    // Validar rango de fechas si es temporal
    if (newDiscount.isTemporal) {
      if (!newDiscount.startDate || !newDiscount.endDate) {
        MySwal.fire('Error', 'Selecciona fecha de inicio y fin.', 'warning');
        return;
      }
      if (newDiscount.endDate < newDiscount.startDate) {
        MySwal.fire('Error', 'La fecha de fin no puede ser anterior al inicio.', 'warning');
        return;
      }
    }

    setLoading(true);
    try {
      const discountRef = doc(firestore, 'discountCodes', codeNormalized);
      const existing = await getDoc(discountRef);
      if (existing.exists()) {
        MySwal.fire('Error', 'Ya existe un código con ese nombre.', 'error');
        setLoading(false);
        return;
      }

      await setDoc(discountRef, {
        code: codeNormalized,
        type: newDiscount.type, // 'percent' o 'fixed'
        value: valueNumber,
        isTemporal: newDiscount.isTemporal,
        startDate: newDiscount.isTemporal ? newDiscount.startDate : '',
        endDate: newDiscount.isTemporal ? newDiscount.endDate : '',
        isEnabled: true,
        createdAt: new Date().toISOString(),
      });

      MySwal.fire('Éxito', '¡Código de descuento creado!', 'success');

      // Recargar lista
      const q = collection(firestore, 'discountCodes');
      const querySnapshot = await getDocs(q);
      const discountsList = querySnapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setDiscounts(discountsList);

      // Reset form
      setNewDiscount({
        code: '',
        type: 'percent',
        value: 0,
        isTemporal: false,
        startDate: '',
        endDate: '',
        isEnabled: true,
      });
    } catch (error) {
      console.error(error);
      MySwal.fire('Error', 'Hubo un problema al crear el código.', 'error');
    } finally {
      setLoading(false);
    }
  };

    const handleSaveDiscount = async (id) => {
    const discount = discounts.find((d) => d.id === id);
    if (!discount) return;

    const valueNumber = Number(discount.value);
    if (!valueNumber || valueNumber <= 0) {
      MySwal.fire('Error', 'Ingresa un valor de descuento válido.', 'warning');
      return;
    }

    if (discount.isTemporal) {
      if (!discount.startDate || !discount.endDate) {
        MySwal.fire('Error', 'Selecciona fecha de inicio y fin.', 'warning');
        return;
      }
      if (discount.endDate < discount.startDate) {
        MySwal.fire('Error', 'La fecha de fin no puede ser anterior al inicio.', 'warning');
        return;
      }
    }

    setLoading(true);
    try {
      const discountRef = doc(firestore, 'discountCodes', id);
      await updateDoc(discountRef, {
        type: discount.type,
        value: valueNumber,
        isTemporal: discount.isTemporal,
        startDate: discount.isTemporal ? discount.startDate || '' : '',
        endDate: discount.isTemporal ? discount.endDate || '' : '',
        isEnabled: discount.isEnabled,
        updatedAt: new Date().toISOString(),
      });

      MySwal.fire('Éxito', 'Código actualizado.', 'success');
    } catch (error) {
      console.error(error);
      MySwal.fire('Error', 'No se pudo actualizar el código.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDiscount = async (id, newStatus) => {
    setLoading(true);
    try {
      const discountRef = doc(firestore, 'discountCodes', id);
      await updateDoc(discountRef, {
        isEnabled: newStatus,
        updatedAt: new Date().toISOString(),
      });

      setDiscounts((prev) =>
        prev.map((d) =>
          d.id === id ? { ...d, isEnabled: newStatus } : d
        )
      );
    } catch (error) {
      console.error(error);
      MySwal.fire('Error', 'No se pudo cambiar el estado del código.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDiscount = async (id) => {
    MySwal.fire({
      title: '¿Eliminar código?',
      text: 'Esta acción no se puede deshacer.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, eliminar',
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        try {
          await deleteDoc(doc(firestore, 'discountCodes', id));
          setDiscounts((prev) => prev.filter((d) => d.id !== id));
          MySwal.fire('Eliminado', 'Código eliminado correctamente.', 'success');
        } catch (error) {
          console.error(error);
          MySwal.fire('Error', 'No se pudo eliminar el código.', 'error');
        } finally {
          setLoading(false);
        }
      }
    });
  };



  const handleDeleteAdmin = async (id) => {
    MySwal.fire({
      title: '¿Estás seguro?',
      text: 'Esto eliminará al admin de la lista.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, ¡elimínalo!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true); // Start loading spinner
        try {
          await deleteDoc(doc(firestore, 'admins', id));
          MySwal.fire('Eliminado', '¡Admin eliminado exitosamente!', 'success');

          // Refresh the admin list
          const q = collection(firestore, 'admins');
          const querySnapshot = await getDocs(q);
          const adminsList = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setAdmins(adminsList);
        } catch (error) {
          MySwal.fire('Error', 'Hubo un problema al eliminar el admin.', 'error');
        } finally {
          setLoading(false); // End loading spinner
        }
      }
    });
  };

  const handleUpdateBusinessSettings = async (updatedSettings) => {
    setLoading(true); // Start loading spinner
    try {
      const docRef = doc(firestore, 'businessSettings', 'main');
      await updateDoc(docRef, updatedSettings);
      setBusinessSettings((prev) => ({ ...prev, ...updatedSettings }));
      MySwal.fire('Éxito', '¡Configuración actualizada exitosamente!', 'success');
    } catch (error) {
      MySwal.fire('Error', 'Hubo un problema al actualizar la configuración.', 'error');
    } finally {
      setLoading(false); // End loading spinner
    }
  };

  const toggleIsManual = () => {
    handleUpdateBusinessSettings({ isManual: !businessSettings.isManual });
    if (!businessSettings.isManual) autoUpdateStoreStatus(businessSettings); // Check status when switching to automatic
  };

  const toggleIsOpen = () => {
    handleUpdateBusinessSettings({ isOpen: !businessSettings.isOpen });
  };

  const updateBusinessHours = () => {
    handleUpdateBusinessSettings({ open: openTime, close: closeTime });
    autoUpdateStoreStatus({ ...businessSettings, open: openTime, close: closeTime });
  };

  if (!isAdmin) {
    return <p>No tienes acceso a esta página.</p>;
  }

  return (
    <div className={styles.adminManagementContainer}>
      {loading && <div className={styles.loadingOverlay}></div>}
      <h2 className={styles.adminManagementTitle}>Admin: Manejo de Cuentas</h2>
      {currentUser && <p className={styles.loggedInUser}>Accediste como: {currentUser.email}</p>}

      <div className={styles.inputGroup}>
        <input
          type="email"
          value={adminEmail}
          onChange={(e) => setAdminEmail(e.target.value)}
          placeholder="Ingresa admin email"
          className={styles.inputField}
        />
        <button className={styles.addButton} onClick={handleAddAdmin}>
          Agregar Admin
        </button>
      </div>

      <h3>Admins Actuales:</h3>
      <ul className={styles.adminList}>
        {admins.map((admin) => (
          <li key={admin.id} className={styles.adminItem}>
            <span className={styles.adminEmail}>{admin.email}</span>
            <button className={styles.removeButton} onClick={() => handleDeleteAdmin(admin.id)}>
              Eliminar
            </button>
          </li>
        ))}
      </ul>

            <h3>Gestión de Códigos de Descuento:</h3>
      <div className={styles.discountSection}>
        <div className={styles.discountForm}>
          <input
            type="text"
            placeholder="Código (ej: AMOR10)"
            value={newDiscount.code}
            onChange={(e) => handleNewDiscountChange('code', e.target.value)}
            className={styles.inputField}
          />

          <div className={styles.discountRow}>
            <label>
              Tipo:
              <select
                value={newDiscount.type}
                onChange={(e) => handleNewDiscountChange('type', e.target.value)}
              >
                <option value="percent">Porcentaje (%)</option>
                <option value="fixed">Monto fijo (CRC)</option>
              </select>
            </label>

            <label>
              Valor:
              <input
                type="number"
                min="1"
                value={newDiscount.value}
                onChange={(e) => handleNewDiscountChange('value', e.target.value)}
              />
            </label>
          </div>

          <div className={styles.discountRow}>
            <label className={styles.switchLabel}>
              <input
                type="checkbox"
                checked={newDiscount.isTemporal}
                onChange={(e) =>
                  handleNewDiscountChange('isTemporal', e.target.checked)
                }
                className={styles.switchInput}
              />
              <span className={styles.switchSlider}></span>
              ¿Es temporal?
            </label>

            {newDiscount.isTemporal && (
              <>
                <label>
                  Inicio:
                  <input
                    type="date"
                    value={newDiscount.startDate}
                    onChange={(e) =>
                      handleNewDiscountChange('startDate', e.target.value)
                    }
                  />
                </label>
                <label>
                  Fin:
                  <input
                    type="date"
                    value={newDiscount.endDate}
                    onChange={(e) =>
                      handleNewDiscountChange('endDate', e.target.value)
                    }
                  />
                </label>
              </>
            )}
          </div>

          <button
            className={styles.addButton}
            onClick={handleAddDiscount}
          >
            Crear Código
          </button>
        </div>

        <ul className={styles.discountList}>
          {discounts.map((d) => (
            <li key={d.id} className={styles.discountItem}>
              <div className={styles.discountHeader}>
                <span className={styles.discountCode}>{d.code}</span>
                <span
                  className={`${styles.discountStatus} ${
                    d.isEnabled ? styles.enabled : styles.disabled
                  }`}
                >
                  {d.isEnabled ? 'Habilitado' : 'Deshabilitado'}
                </span>
              </div>

              <div className={styles.discountRow}>
                <label>
                  Tipo:
                  <select
                    value={d.type}
                    onChange={(e) =>
                      handleDiscountFieldChange(d.id, 'type', e.target.value)
                    }
                  >
                    <option value="percent">Porcentaje (%)</option>
                    <option value="fixed">Monto fijo (CRC)</option>
                  </select>
                </label>

                <label>
                  Valor:
                  <input
                    type="number"
                    min="1"
                    value={d.value}
                    onChange={(e) =>
                      handleDiscountFieldChange(d.id, 'value', e.target.value)
                    }
                  />
                </label>
              </div>

              <div className={styles.discountRow}>
                <label className={styles.switchLabel}>
                  <input
                    type="checkbox"
                    checked={d.isTemporal}
                    onChange={(e) =>
                      handleDiscountFieldChange(d.id, 'isTemporal', e.target.checked)
                    }
                    className={styles.switchInput}
                  />
                  <span className={styles.switchSlider}></span>
                  ¿Es temporal?
                </label>

                {d.isTemporal && (
                  <>
                    <label>
                      Inicio:
                      <input
                        type="date"
                        value={d.startDate || ''}
                        onChange={(e) =>
                          handleDiscountFieldChange(d.id, 'startDate', e.target.value)
                        }
                      />
                    </label>
                    <label>
                      Fin:
                      <input
                        type="date"
                        value={d.endDate || ''}
                        onChange={(e) =>
                          handleDiscountFieldChange(d.id, 'endDate', e.target.value)
                        }
                      />
                    </label>
                  </>
                )}
              </div>

              <div className={styles.discountButtons}>
                <button
                  className={styles.updateButton}
                  onClick={() => handleSaveDiscount(d.id)}
                >
                  Guardar
                </button>
                <button
                  className={styles.toggleButton}
                  onClick={() => handleToggleDiscount(d.id, !d.isEnabled)}
                >
                  {d.isEnabled ? 'Deshabilitar' : 'Habilitar'}
                </button>
                <button
                  className={styles.removeButton}
                  onClick={() => handleDeleteDiscount(d.id)}
                >
                  Eliminar
                </button>
              </div>
            </li>
          ))}
        </ul>
      </div>


      <h3>Configuración de Horarios:</h3>
      <div className={styles.businessSettings}>
        <label className={styles.switchLabel}>
          <input
            type="checkbox"
            checked={businessSettings.isManual}
            onChange={toggleIsManual}
            className={styles.switchInput}
          />
          <span className={styles.switchSlider}></span>
          Modo Manual
        </label>
        {businessSettings.isManual && (
          <label className={styles.switchLabel}>
            <input
              type="checkbox"
              checked={businessSettings.isOpen}
              onChange={toggleIsOpen}
              className={styles.switchInput}
            />
            <span className={styles.switchSlider}></span>
            ¿Abierto?
          </label>
        )}
        {!businessSettings.isManual && (
          <div className={styles.timeSettings}>
            <label>
              Hora de Apertura:
              <input
                type="time"
                value={openTime}
                onChange={(e) => setOpenTime(e.target.value)}
                className={styles.timeInput}
              />
            </label>
            <label>
              Hora de Cierre:
              <input
                type="time"
                value={closeTime}
                onChange={(e) => setCloseTime(e.target.value)}
                className={styles.timeInput}
              />
            </label>
            <button onClick={updateBusinessHours} className={styles.updateButton}>
              Actualizar Horarios
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminManagement;
